import { Button, Modal, Tag, Text } from "@ttc3k/trekker";
import { PageHeader, PageSidebar } from "shared/components/layouts";
import { EmptyState } from "shared/components/root";
import { useOperatorModal } from "./useOperatorModal";
import {
  formatPhoneNumber,
  getAddressString,
  getMultilingualString,
} from "shared/utils";
import { SimpleErrorNotification, SimpleLoader } from "shared/components/atoms";
import { scale } from "@cloudinary/transformation-builder-sdk/actions/resize";
import { useTranslation } from "react-i18next";
import { Box, Center, Flex } from "styled-system/jsx";
import { OpenNewWindow } from "iconoir-react";
import {
  AnimatedPage,
  Collapsible,
  CopyToClipboard,
} from "shared/components/molecules";
import { styled } from "styled-system/jsx";
import { StaffPageContent } from "../StaffPageContent";
import { EnumEntityReferenceType, useOperatorStaffQuery } from "gql/generated";
import { Notes } from "../Notes";
import { ViewSubSection } from "shared/components/app";
import { AnimatePresence } from "framer-motion";
import { StaticMap } from "shared/components";
import { useEffect, useState } from "react";

type OperatorModalProps = {
  isOpen: boolean;
  operatorId: string;
  onClose: () => void;
};

export const OperatorModal = ({
  isOpen,
  operatorId,
  onClose,
}: OperatorModalProps) => {
  const { t } = useTranslation();
  const {
    operator,
    error,
    loading,
    socialLinks,
    tagProps,
    handleSocialClick,
    handleViewInDirectoryClick,
  } = useOperatorModal({ operatorId });
  const coordinates: [number, number] = [
    operator?.contact.address?.location?.coordinates[0] ?? 0,
    operator?.contact.address?.location?.coordinates[1] ?? 0,
  ];
  const isValidCoordinates = coordinates[0] !== 0 && coordinates[1] !== 0;

  // Assume the operator has notes and staff
  // This is required because loading of notes and staff
  // occurs directly in the child <Notes> and <StaffPageContent> components
  const [noteCount, setNoteCount] = useState(0);
  const [staffCount, setStaffCount] = useState(0);
  useEffect(() => {
    setNoteCount(0);
    setStaffCount(0);
  }, [operator, setNoteCount, setStaffCount]);

  return (
    <Modal
      open={isOpen}
      placement={"centerPane"}
      onOpenChange={onClose}
      closeTriggerVisual={"subtle"}
      closeTriggerPlacement="booker"
      noMaxH
      portal
      userSelect="none"
    >
      {loading && isOpen ? (
        <Center h="85vh" w="891px">
          <SimpleLoader />
        </Center>
      ) : error && isOpen ? (
        <SimpleErrorNotification />
      ) : operator ? (
        <Flex>
          <Flex
            bg={"bg.wa.lightest"}
            borderRightWidth={"1px"}
            borderRightColor={"border.light"}
            shrink={0}
          >
            <Box
              pt={"60px"}
              pb={"30px"}
              px={"600"}
              maxH="85vh"
              h="85vh"
              overflowY="auto"
            >
              <AnimatePresence mode={"wait"}>
                <AnimatedPage initial={"exit"}>
                  <PageSidebar
                    hideBackButton={true}
                    title={getMultilingualString(operator.name)}
                    tagProps={tagProps}
                    imageProps={{
                      image: operator.images[0],
                      resize: scale().height(260),
                    }}
                    primaryButtonProps={{
                      visual: "primary",
                      mode: "light",
                      children: t("core:OPERATOR_MODAL.CTA_BUTTON_COPY"),
                      Icon: OpenNewWindow,
                      isIconRightAligned: true,
                      onClick: handleViewInDirectoryClick,
                    }}
                  >
                    <Flex flexDir={"column"} gap={"150"}>
                      {/* Address */}
                      {operator.contact.address && (
                        <Flex flexDir="column" gap="50">
                          <Text visual="smallSemiBold" color="text.light">
                            {t("core:CSV.HEADERS.ADDRESS")}
                          </Text>
                          <Text visual={"bodyMedium"} color={"text.mid"}>
                            {getAddressString(operator.contact.address)}
                          </Text>
                        </Flex>
                      )}

                      {/* Phone and Email */}
                      <Flex flexDir="column" gap="50">
                        <Text visual="smallSemiBold" color="text.light">
                          {t(
                            "core:OPERATOR_MODAL.HEADINGS.BUSINESS_CONTACT_INFO",
                          )}
                        </Text>
                        {operator.contact.phone && (
                          <CopyToClipboard
                            text={formatPhoneNumber(operator.contact.phone)}
                            visual="bodyMedium"
                            color="text.mid"
                            opacity={1}
                          />
                        )}
                        {operator.contact.email && (
                          <CopyToClipboard
                            text={operator.contact.email}
                            visual="bodyMedium"
                            color="text.mid"
                            opacity={1}
                          />
                        )}
                      </Flex>
                    </Flex>
                    {/* Social links */}
                    <Flex
                      flexDir="row"
                      justifyContent={"flex-start"}
                      alignItems={"center"}
                      gap={"200"}
                      flexWrap={"wrap"}
                    >
                      {socialLinks.map((link) => (
                        <Button
                          visual="unstyled"
                          key={link.alt}
                          rounded="25"
                          onClick={() => handleSocialClick(link)}
                          title={link.alt}
                        >
                          <styled.img
                            src={`/icons/${link.Icon}`}
                            alt={link.alt}
                            w={"24px"}
                            h={"24px"}
                          />
                        </Button>
                      ))}
                    </Flex>
                  </PageSidebar>
                </AnimatedPage>
              </AnimatePresence>
            </Box>
          </Flex>
          <Flex bg={"bg.wa.light"} grow={1}>
            <Box
              py={"60px"}
              px={"60px"}
              maxH="85vh"
              h="85vh"
              overflowY="auto"
              w={"full"}
            >
              <Box w={"400px"}>
                <Flex
                  direction={"column"}
                  h={"fit-content"}
                  w={"full"}
                  gap={"300"}
                >
                  <PageHeader
                    title={t("core:OPERATOR_MODAL.HEADINGS.OVERVIEW")}
                  />

                  {/* Description */}
                  <Text visual="bodyRegular" color="text.mid">
                    {getMultilingualString(operator.description ?? {})}
                  </Text>

                  {/* Mapbox Map */}
                  {isValidCoordinates && (
                    <StaticMap
                      latitude={coordinates[1]}
                      longitude={coordinates[0]}
                      width={400}
                    />
                  )}

                  {/* Staff */}
                  <Collapsible open={staffCount > 0}>
                    <ViewSubSection title={t("core:CSV.HEADERS.STAFF")}>
                      <StaffPageContent
                        _entityId={operatorId}
                        type={EnumEntityReferenceType.Operator}
                        viewOnly={true}
                        forceSingleColumn={true}
                        setInviteStaffModalClickHandler={() => {}}
                        staffGetter={useOperatorStaffQuery}
                        setStaffCount={setStaffCount}
                      />
                    </ViewSubSection>
                  </Collapsible>

                  {/* Back-office tags */}
                  {operator.tags?.organizationSpecific &&
                    operator.tags?.organizationSpecific.length > 0 && (
                      <ViewSubSection
                        title={t("core:CSV.HEADERS.BACK_OFFICE_TAGS")}
                      >
                        <Flex flexDir="row" gap="75" flexWrap="wrap">
                          {operator.tags?.organizationSpecific.map((tag) => (
                            <Tag key={tag._id} visual="warning" mode="light">
                              {getMultilingualString(tag.name ?? {})}
                            </Tag>
                          ))}
                        </Flex>
                      </ViewSubSection>
                    )}

                  {/* Notes */}
                  <Collapsible open={noteCount > 0}>
                    <ViewSubSection
                      title={t("core:OPERATOR_MODAL.HEADINGS.NOTES")}
                    >
                      <Notes
                        _entityId={operatorId}
                        type={EnumEntityReferenceType.Operator}
                        viewOnly={true}
                        forceSingleColumn={true}
                        getNoteCreatingState={() => {}}
                        setNoteCount={setNoteCount}
                      />
                    </ViewSubSection>
                  </Collapsible>
                </Flex>
              </Box>
            </Box>
          </Flex>
        </Flex>
      ) : (
        <EmptyState h="85vh" w="891px" />
      )}
    </Modal>
  );
};
